'use client';

import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { slate } from '@/lib/colours';
import { Box, CircularProgress, Stack, Typography } from '@/primitives';
import { formatDate } from '@/lib/dateTime';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';
import type { GetSalesQuery } from '@/publicSearch/graphql/queries.generated';

type SaleDetail = NonNullable<GetSalesQuery['sales']['items'][number]>;

type SaleInfoPanelProps = {
  loading: boolean;
  saleDetail: SaleDetail | null;
};

export default function SaleInfoPanel({
  loading,
  saleDetail,
}: SaleInfoPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const dateFormat = useSocietyDateFormat();

  if (loading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          py: 2,
          mb: 1.5,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!saleDetail) return null;

  return (
    <Box
      sx={{
        mb: 1.5,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          minHeight: 48,
          px: 2,
          gap: 1.5,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded((prev) => !prev);
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 1,
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {saleDetail.saleName}
          </Typography>
          {saleDetail.saleDateDescription && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                mt: 0.25,
              }}
            >
              {saleDetail.saleDateDescription}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={
            expanded ? 'Collapse sale details' : 'Expand sale details'
          }
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            flexShrink: 0,
            ml: 0.5,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
        <Stack
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            px: 2,
            pt: 0,
            pb: 2,
          }}
        >
          {saleDetail.saleLocation && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: slate[400], fontSize: '12px', mb: 0.5 }}>
                Location
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {saleDetail.saleLocation}
              </Typography>
            </Box>
          )}
          {saleDetail.saleDate && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: slate[400], fontSize: '12px', mb: 0.5 }}>
                Sale Date
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {formatDate(new Date(saleDetail.saleDate), dateFormat)}
              </Typography>
            </Box>
          )}
          {saleDetail.lotCount != null && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: slate[400], fontSize: '12px', mb: 0.5 }}>
                Lots
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {saleDetail.lotCount}
              </Typography>
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}
